import useSWR from "swr";
import api, { axiosFetcher } from "@/utils/axios-util";
import * as Sentry from "@sentry/nextjs";

export const useIntake = (emailorPhone: string) => {
  const { data, error, mutate } = useSWR(
    emailorPhone ? `${process.env.TL_API}/intake/${emailorPhone}` : null,
    axiosFetcher,
  );

  return {
    data: data || {},
    isLoading: !error && !data,
    error,
    mutate,
  };
};

export const addWeightLoss = async (params: any, token?: string) => {
  let result: any = undefined;
  if (token && token !== "") {
    result = await api.post(`${process.env.TL_API}/intake/weightloss`, params, {
      headers: {
        Authorization: `Bearer ${token}`,
        accept: "application/json",
      },
    });
  } else {
    await api.post(`${process.env.TL_API}/intake/weightloss`, params);
  }
  const { data, status } = result;
  return {
    ...(data || {}),
    status,
    isLoading: false,
    timeout: status === 401,
    isError: status !== 201,
  };
};

export const addAllergies = async (params: any, token: string) => {
  const result = await api.post(
    `${process.env.TL_API}/intake/allergy`,
    params,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        accept: "application/json",
      },
    },
  );
  const { data, status } = result;
  return {
    ...(data || {}),
    status,
    isLoading: false,
    isError: status !== 201,
  };
};

export const addIntake = async (params: any, token: string) => {
  const result = await api.post(`${process.env.TL_API}/intake`, params, {
    headers: {
      Authorization: `Bearer ${token}`,
      accept: "application/json",
    },
  });
  const { data, status } = result;
  return {
    ...(data || {}),
    status,
    isLoading: false,
    isError: status !== 201,
  };
};

export const getIntake = async (emailorPhone: string, token: string) => {
  if (!emailorPhone)
    return { data: {}, status: 200, isLoading: false, isError: false };
  const result = await api.get(`${process.env.TL_API}/intake/${emailorPhone}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      accept: "application/json",
    },
  });

  const { data, status } = result;

  return {
    data,
    status,
    isLoading: false,
    isError: status === 401,
  };
};

export const getOtp = async (params: any) => {
  const result = await api.post(`${process.env.TL_API}/patient/2fa`, params);
  const { data, status } = result;
  return {
    data,
    status,
    isLoading: false,
    isError: status !== 200,
  };
};

export const refreshToken = async (token: string) => {
  const result = await api.post(`${process.env.TL_API}/patient/refresh`, {
    token,
  });
  const { data, status } = result;
  return {
    data,
    status,
    isLoading: false,
    isError: status !== 200,
  };
};

export const verifyOtp = async (params: any) => {
  const result = await api.post(
    `${process.env.TL_API}/patient/2fa/verify`,
    params,
  );
  const { data, status } = result;
  return {
    data,
    status,
    isLoading: false,
    isError: status !== 200,
  };
};

export const getPayors = async (token: string) => {
  const result = await api.get(`${process.env.TL_API}/insurance/list/payors`, {
    headers: {
      Authorization: `Bearer ${token}`,
      accept: "application/json",
    },
  });
  const { data, status } = result;
  return {
    data,
    status,
    isLoading: false,
    isError: status !== 200,
  };
};

export const saveInsurance = async (params: any, token: string) => {
  const result = await api.post(
    `${process.env.TL_API}/intake/insurance`,
    params,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        accept: "application/json",
      },
    },
  );
  const { data, status } = result;
  return {
    data,
    status,
    isLoading: false,
    isError: status !== 200,
  };
};

export const saveLicense = async (params: any, token: string) => {
  try {
    Sentry.addBreadcrumb({
      category: "file-upload",
      message: "License upload started",
      level: "info",
      data: {
        email: params.email,
        type: params?.type ?? "",
        extension: params?.extension,
        fileSize: params.file?.size,
        fileName: params.file?.name,
      },
    });

    const formData = new FormData();
    formData.append("file", params.file);
    formData.append("email", params.email);
    formData.append("type", params?.type ?? "");
    formData.append("extension", params?.extension);
    formData.append("number", params?.number);
    const result = await api.post(
      `${process.env.TL_API}/license/v2`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
          accept: "application/json",
        },
      },
    );
    const { data, status } = result;

    Sentry.addBreadcrumb({
      category: "file-upload",
      message: "License upload completed successfully",
      level: "info",
      data: {
        status,
        email: params.email,
      },
    });

    return {
      data,
      status,
      isLoading: false,
      isError: status !== 201,
    };
  } catch (e) {
    Sentry.captureException(e, {
      tags: {
        function: "saveLicense",
        uploadType: "license",
      },
      extra: {
        email: params.email,
        type: params?.type ?? "",
        extension: params?.extension,
        fileSize: params.file?.size,
        fileName: params.file?.name,
      },
    });

    return {
      data: e,
      status: 500,
      isLoading: false,
      isError: true,
    };
  }
};

export const getLicense = async (filename: string, token: string) => {
  const result = await api.get(
    `${process.env.TL_API}/intake/${filename}/license`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        accept: "application/json",
      },
    },
  );
  const { data, status } = result;
  return {
    data,
    status,
    isLoading: false,
    isError: status !== 200,
  };
};

export const updateIntake = async (id: number, params: any, token: string) => {
  const result = await api.put(`${process.env.TL_API}/intake/${id}`, params, {
    headers: {
      Authorization: `Bearer ${token}`,
      accept: "application/json",
    },
  });
  const { data, status } = result;
  return {
    data,
    status,
    timeout: status === 401,
    isLoading: false,
    isError: status !== 200,
  };
};

export const getAllergies = async (name: string) => {
  if (name === "" || name === undefined)
    return { data: [], status: 200, isLoading: false, isError: false };

  const result = await api.get(`${process.env.TL_API}/allergies/${name}`);
  const { data, status } = result;

  return {
    data,
    status,
    isLoading: false,
    isError: status !== 200,
  };
};

export const saveAllergies = async (name: string[]) => {
  const result = await api.post(`${process.env.TL_API}/allergies`, name);
  const { data, status } = result;

  return {
    data,
    status,
    isLoading: false,
    isError: status !== 201,
  };
};
